<script setup>
import { onMounted, computed, ref, reactive } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();

const records = useMapGetter('rawData/getRecords');
const uiFlags = useMapGetter('rawData/getUIFlags');
const meta = useMapGetter('rawData/getMeta');
const inboxes = useMapGetter('inboxes/getInboxes');

const searchQuery = ref('');
const statusFilter = ref('');
const selectedIds = ref([]);
const resolveInboxId = ref('');
const showImportModal = ref(false);
const showCreateModal = ref(false);
const importFileData = ref(null);
const editingRecord = ref(null);
const sortField = ref('created_at');
const sortDirection = ref('desc');

const form = reactive({
  name: '',
  email: '',
  phone_number: '',
  source: 'manual',
});

const currentPage = computed(() => meta.value?.currentPage || 1);
const totalItems = computed(() => meta.value?.totalCount || 0);
const hasRecords = computed(() => records.value.length > 0);
const hasSelection = computed(() => selectedIds.value.length > 0);
const allSelected = computed(() =>
  records.value.length > 0 && selectedIds.value.length === records.value.length
);

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolving', label: 'Resolving' },
  { value: 'sending', label: 'Sending' },
  { value: 'sent', label: 'Sent' },
  { value: 'replied', label: 'Replied' },
  { value: 'no_reply', label: 'No Reply' },
  { value: 'failed', label: 'Failed' },
];

const statusColorMap = {
  pending: 'bg-n-slate-3 text-n-slate-12',
  resolving: 'bg-n-amber-3 text-n-amber-11',
  sending: 'bg-n-blue-3 text-n-blue-11',
  sent: 'bg-n-green-3 text-n-green-11',
  replied: 'bg-n-iris-3 text-n-iris-11',
  no_reply: 'bg-n-slate-3 text-n-slate-11',
  failed: 'bg-n-ruby-3 text-n-ruby-11',
};

const fetchData = (page = 1) => {
  const params = {
    page,
    sort: sortField.value,
    direction: sortDirection.value,
  };
  if (statusFilter.value) params.status = statusFilter.value;
  if (searchQuery.value) params.q = searchQuery.value;
  store.dispatch('rawData/get', params);
};

const onSearch = () => fetchData();
const onFilter = () => fetchData();
const onPageChange = page => fetchData(page);

const toggleSort = field => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortDirection.value = 'asc';
  }
  fetchData();
};

const toggleSelectAll = () => {
  selectedIds.value = allSelected.value
    ? []
    : records.value.map(r => r.id);
};

const toggleSelect = id => {
  const idx = selectedIds.value.indexOf(id);
  if (idx === -1) selectedIds.value.push(id);
  else selectedIds.value.splice(idx, 1);
};

const statusClass = status => statusColorMap[status] || 'bg-n-slate-3 text-n-slate-11';

const channelKeys = record => {
  if (!record.channel_identifiers) return [];
  return Object.keys(record.channel_identifiers);
};

const formatDate = dateStr => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString();
};

const editRecord = record => {
  editingRecord.value = record;
  Object.assign(form, {
    name: record.name || '',
    email: record.email || '',
    phone_number: record.phone_number || '',
    source: record.source || 'manual',
  });
  showCreateModal.value = true;
};

const closeCreateModal = () => {
  showCreateModal.value = false;
  editingRecord.value = null;
  Object.assign(form, { name: '', email: '', phone_number: '', source: 'manual' });
};

const saveRecord = async () => {
  try {
    if (editingRecord.value) {
      await store.dispatch('rawData/update', { id: editingRecord.value.id, raw_datum: { ...form } });
      useAlert('Record updated successfully');
    } else {
      await store.dispatch('rawData/create', { raw_datum: { ...form } });
      useAlert('Record created successfully');
    }
    closeCreateModal();
    fetchData();
  } catch {
    useAlert('Failed to save record');
  }
};

const deleteRecord = async id => {
  try {
    await store.dispatch('rawData/delete', id);
    useAlert('Record deleted');
  } catch {
    useAlert('Failed to delete record');
  }
};

const onFileSelect = e => {
  importFileData.value = e.target.files[0];
};

const importFile = async () => {
  if (!importFileData.value) return;
  try {
    await store.dispatch('rawData/importFile', { file: importFileData.value, source: 'csv' });
    useAlert('Import started in background');
    showImportModal.value = false;
    importFileData.value = null;
    setTimeout(() => fetchData(), 2000);
  } catch {
    useAlert('Import failed');
  }
};

const bulkResolve = async () => {
  if (!resolveInboxId.value || !selectedIds.value.length) return;
  try {
    await store.dispatch('rawData/bulkResolve', {
      inboxId: resolveInboxId.value,
      rawDatumIds: selectedIds.value,
    });
    useAlert(`Resolving ${selectedIds.value.length} records`);
    selectedIds.value = [];
  } catch {
    useAlert('Bulk resolve failed');
  }
};

onMounted(() => {
  fetchData();
  store.dispatch('inboxes/get');
});
</script>

<template>
  <div class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-surface-1">
    <!-- Header -->
    <header class="sticky top-0 z-10 bg-n-surface-1 border-b border-n-weak">
      <div class="flex items-center justify-between px-6 py-3">
        <div class="flex items-center gap-2">
          <h1 class="text-xl font-medium text-n-slate-12">Raw Data</h1>
          <span
            v-if="totalItems"
            class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-slate-3 text-n-slate-11"
          >
            {{ totalItems }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <button
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-n-weak text-n-slate-12 bg-n-surface-1 hover:bg-n-slate-3 transition-colors"
            @click="showImportModal = true"
          >
            <i class="i-lucide-upload size-4" />
            Import
          </button>
          <button
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-n-brand text-white hover:opacity-90 transition-colors"
            @click="showCreateModal = true"
          >
            <i class="i-lucide-plus size-4" />
            Add Record
          </button>
        </div>
      </div>
      <!-- Search & Filter bar -->
      <div class="flex items-center gap-3 px-6 pb-3">
        <div class="relative flex-1 max-w-sm">
          <i class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-n-slate-9" />
          <input
            v-model="searchQuery"
            type="text"
            class="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand"
            placeholder="Search name, email, phone..."
            @keyup.enter="onSearch"
          />
        </div>
        <select
          v-model="statusFilter"
          class="px-3 py-1.5 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
          @change="onFilter"
        >
          <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>
      </div>
    </header>

    <!-- Bulk Actions Bar -->
    <div
      v-if="hasSelection"
      class="flex items-center gap-3 px-6 py-2 bg-n-blue-2 border-b border-n-blue-6"
    >
      <span class="text-sm font-medium text-n-blue-11">
        {{ selectedIds.length }} selected
      </span>
      <select
        v-model="resolveInboxId"
        class="px-2 py-1 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12"
      >
        <option value="">Select Inbox</option>
        <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>
      <button
        class="px-3 py-1 text-sm font-medium rounded-lg bg-n-brand text-white hover:opacity-90 disabled:opacity-50 transition-colors"
        :disabled="!resolveInboxId"
        @click="bulkResolve"
      >
        Resolve Identities
      </button>
      <button
        class="px-3 py-1 text-sm rounded-lg text-n-slate-11 hover:text-n-slate-12"
        @click="selectedIds = []"
      >
        Clear
      </button>
    </div>

    <!-- Table -->
    <main class="flex-1 overflow-y-auto px-6">
      <div class="w-full mx-auto max-w-6xl">
        <div v-if="uiFlags.isFetching" class="flex items-center justify-center py-10 text-n-slate-11">
          <Spinner />
        </div>

        <template v-else>
          <table v-if="hasRecords" class="w-full text-sm">
            <thead>
              <tr class="border-b border-n-weak text-n-slate-11">
                <th class="py-3 pl-0 pr-2 w-8">
                  <input
                    type="checkbox"
                    :checked="allSelected"
                    class="rounded border-n-weak"
                    @change="toggleSelectAll"
                  />
                </th>
                <th
                  class="py-3 px-2 text-left font-medium cursor-pointer hover:text-n-slate-12 select-none"
                  @click="toggleSort('name')"
                >
                  Name
                  <span v-if="sortField === 'name'" class="ml-1 text-xs">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
                </th>
                <th
                  class="py-3 px-2 text-left font-medium cursor-pointer hover:text-n-slate-12 select-none"
                  @click="toggleSort('email')"
                >
                  Email
                </th>
                <th class="py-3 px-2 text-left font-medium">Phone</th>
                <th
                  class="py-3 px-2 text-left font-medium cursor-pointer hover:text-n-slate-12 select-none"
                  @click="toggleSort('source')"
                >
                  Source
                </th>
                <th
                  class="py-3 px-2 text-left font-medium cursor-pointer hover:text-n-slate-12 select-none"
                  @click="toggleSort('status')"
                >
                  Status
                </th>
                <th class="py-3 px-2 text-left font-medium">Channels</th>
                <th class="py-3 px-2 text-left font-medium">Created</th>
                <th class="py-3 px-2 text-right font-medium w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="record in records"
                :key="record.id"
                class="border-b border-n-weak/50 hover:bg-n-slate-2 transition-colors"
              >
                <td class="py-2.5 pl-0 pr-2">
                  <input
                    type="checkbox"
                    :checked="selectedIds.includes(record.id)"
                    class="rounded border-n-weak"
                    @change="toggleSelect(record.id)"
                  />
                </td>
                <td class="py-2.5 px-2">
                  <div class="font-medium text-n-slate-12">{{ record.name || '—' }}</div>
                  <div v-if="record.last_name" class="text-xs text-n-slate-9">{{ record.last_name }}</div>
                </td>
                <td class="py-2.5 px-2 text-n-slate-11">{{ record.email || '—' }}</td>
                <td class="py-2.5 px-2 text-n-slate-11">{{ record.phone_number || '—' }}</td>
                <td class="py-2.5 px-2">
                  <span
                    v-if="record.source"
                    class="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-n-iris-3 text-n-iris-11"
                  >
                    {{ record.source }}
                  </span>
                  <span v-else class="text-n-slate-9">—</span>
                </td>
                <td class="py-2.5 px-2">
                  <span
                    class="inline-block px-2 py-0.5 text-xs font-medium rounded-full"
                    :class="statusClass(record.status)"
                  >
                    {{ record.status }}
                  </span>
                </td>
                <td class="py-2.5 px-2">
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="ch in channelKeys(record)"
                      :key="ch"
                      class="inline-block px-1.5 py-0.5 text-xs rounded bg-n-blue-3 text-n-blue-11"
                    >
                      {{ ch }}
                    </span>
                  </div>
                </td>
                <td class="py-2.5 px-2 text-n-slate-11 text-xs">{{ formatDate(record.created_at) }}</td>
                <td class="py-2.5 px-2 text-right">
                  <div class="flex items-center justify-end gap-1">
                    <button
                      class="p-1 rounded hover:bg-n-slate-3 text-n-slate-9 hover:text-n-slate-12 transition-colors"
                      title="Edit"
                      @click="editRecord(record)"
                    >
                      <i class="i-lucide-pencil size-3.5" />
                    </button>
                    <button
                      class="p-1 rounded hover:bg-n-ruby-3 text-n-slate-9 hover:text-n-ruby-11 transition-colors"
                      title="Delete"
                      @click="deleteRecord(record.id)"
                    >
                      <i class="i-lucide-trash-2 size-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Empty state -->
          <div v-else class="flex flex-col items-center justify-center py-20 text-center">
            <div class="flex items-center justify-center w-16 h-16 mb-4 rounded-2xl bg-n-slate-3">
              <i class="i-lucide-database size-8 text-n-slate-9" />
            </div>
            <h3 class="text-base font-medium text-n-slate-12 mb-1">No raw data records</h3>
            <p class="text-sm text-n-slate-9 mb-4">Import a CSV or add records manually to get started.</p>
            <div class="flex gap-2">
              <button
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-slate-3"
                @click="showImportModal = true"
              >
                <i class="i-lucide-upload size-4" />
                Import CSV
              </button>
              <button
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-n-brand text-white hover:opacity-90"
                @click="showCreateModal = true"
              >
                <i class="i-lucide-plus size-4" />
                Add Record
              </button>
            </div>
          </div>
        </template>
      </div>
    </main>

    <!-- Pagination -->
    <footer v-if="totalItems > 15 && !uiFlags.isFetching" class="sticky bottom-0 z-0">
      <PaginationFooter
        current-page-info="Showing {start} - {end} of {total}"
        :current-page="currentPage"
        :total-items="totalItems"
        :items-per-page="15"
        class="max-w-6xl"
        @update:current-page="onPageChange"
      />
    </footer>

    <!-- Import Modal -->
    <Teleport to="body">
      <div
        v-if="showImportModal"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-n-alpha-black-5"
        @click.self="showImportModal = false"
      >
        <div class="w-full max-w-md rounded-xl bg-n-surface-1 border border-n-weak shadow-xl p-6">
          <h2 class="text-lg font-medium text-n-slate-12 mb-1">Import Raw Data</h2>
          <p class="text-sm text-n-slate-9 mb-4">
            Upload a CSV with columns: name, email, phone_number, source, etc.
          </p>
          <input
            type="file"
            accept=".csv"
            class="w-full text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-n-slate-3 file:text-n-slate-12 hover:file:bg-n-slate-4"
            @change="onFileSelect"
          />
          <div class="flex justify-end gap-2 mt-6">
            <button
              class="px-4 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-slate-3"
              @click="showImportModal = false"
            >
              Cancel
            </button>
            <button
              class="px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:opacity-90 disabled:opacity-50"
              :disabled="!importFileData"
              @click="importFile"
            >
              Import
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Create/Edit Modal -->
    <Teleport to="body">
      <div
        v-if="showCreateModal"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-n-alpha-black-5"
        @click.self="closeCreateModal"
      >
        <div class="w-full max-w-md rounded-xl bg-n-surface-1 border border-n-weak shadow-xl p-6">
          <h2 class="text-lg font-medium text-n-slate-12 mb-4">
            {{ editingRecord ? 'Edit Record' : 'Add Record' }}
          </h2>
          <div class="flex flex-col gap-3">
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-11">Name</label>
              <input
                v-model="form.name"
                type="text"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-11">Email</label>
              <input
                v-model="form.email"
                type="email"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-11">Phone Number</label>
              <input
                v-model="form.phone_number"
                type="text"
                placeholder="+84..."
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-11">Source</label>
              <input
                v-model="form.source"
                type="text"
                placeholder="csv, api, manual..."
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
            </div>
          </div>
          <div class="flex justify-end gap-2 mt-6">
            <button
              class="px-4 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-slate-3"
              @click="closeCreateModal"
            >
              Cancel
            </button>
            <button
              class="px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:opacity-90"
              @click="saveRecord"
            >
              {{ editingRecord ? 'Update' : 'Create' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
